import { useCallback, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import { useQuizList } from '@/views/build/components/useQuizList';
import { MAX_QUIZ_COUNT } from '@/views/build/constant';
import { useBuildInfoContext } from '@/views/build/context/BuildInfoProvider';
import { useBuildFormContext } from '@/views/build/form/schema';
import { findSubjectName } from '@/views/build/util/findSubjectName';
import { getAddedQuizBankItemIds } from '@/views/build/util/getAddedQuizBankItemIds';
import { toBuildQuizListItems } from '@/views/build/util/toBuildQuizListItems';

import { quizBankModal } from '../index';
import { isQuizBankSupported, toQuizBankCategoryTreeId } from '../utils';

/**
 * 빌드 편집 화면에서 문제 은행을 다시 여는 훅. 노출 판정은 모달과 같은 식
 * (`isQuizBankSupported`)을 쓰고, 카테고리를 `useWatch` 로 구독해 학년·과목을
 * 바꾸면 그 자리에서 반영된다.
 */
export const useShowQuizBankModal = () => {
  const { control, getValues } = useBuildFormContext();
  const { subjectTree, quizBankExposedCategoryPrefixes } =
    useBuildInfoContext();
  const { quizList, handleInsert } = useQuizList();

  const [subject, subSubject] = useWatch({
    control,
    name: ['subject', 'subSubject'],
  });

  const categoryTreeId = subSubject || subject || '';
  const isSupported = isQuizBankSupported(categoryTreeId, {
    exposedCategoryPrefixes: quizBankExposedCategoryPrefixes,
  });

  const remainingSlots = Math.max(0, MAX_QUIZ_COUNT - quizList.length);

  /*
    진입 버튼을 빠르게 두 번 누르면 모달이 둘 열린다. 두 모달은 각자 열릴 때의
    `quizList.length`·`addedProblemIds` 를 들고 있어 같은 문항을 중복으로 담거나
    `MAX_QUIZ_COUNT` 를 넘길 수 있다. 설정 모달(`useShowQuizSetupModal`)과 같은 가드를 둔다.
  */
  const isOpened = useRef(false);

  const open = useCallback(async () => {
    if (!isSupported || remainingSlots === 0) return;
    if (isOpened.current) return;
    isOpened.current = true;

    try {
      const problems = await quizBankModal.openAsync({
        categoryTreeId: toQuizBankCategoryTreeId(categoryTreeId),
        maxSelectableCount: remainingSlots,
        addedProblemIds: getAddedQuizBankItemIds(getValues('quizList')),
      });

      if (problems.length === 0) return;

      // useFieldArray 의 insert 를 써야 RHF 상태와 카드 인스턴스가 어긋나지 않는다.
      handleInsert(
        quizList.length,
        toBuildQuizListItems(problems, {
          subject:
            findSubjectName(subjectTree, getValues('subject') ?? '') ?? '',
        }),
      );
    } finally {
      isOpened.current = false;
    }
  }, [
    isSupported,
    remainingSlots,
    categoryTreeId,
    handleInsert,
    quizList.length,
    getValues,
    subjectTree,
  ]);

  return { isSupported, remainingSlots, open };
};
